import { IconEye, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, FilterFn } from "@tanstack/react-table";
import { Flex, Grid, Input, Select, Typography } from "antd";
import { useMemo } from "react";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import { confirmDeleteResource } from "@/common/confirm";
import { type ArtemisReport, ReportStatus, ReportType } from "@/common/model";
import { CustomIcon, DateField } from "../base";
import type { KanbanConfig } from "../kanban-board";
import type { ResponsiveTableProps } from "../responsive-table";
import { ResponsiveTableKanban } from "../responsive-table-kanban";
import { TableFilterBar } from "../table-filter-bar";
import {
	ReportStatusBadge,
	ReportStatusLabelMap,
	ReportTypeBadge,
	ReportTypeLabelMap,
} from "./report-badges";

const searchFilter: FilterFn<ArtemisReport> = (
	row,
	_columnId,
	value: string,
) => {
	const v = value.toLowerCase();
	const found =
		row.original.title.toLowerCase().includes(v) ||
		(row.original.id as string).toLowerCase().includes(v);
	return found;
};

// One board column per report type.
const KANBAN: KanbanConfig<ArtemisReport, ReportType> = {
	columns: Object.values(ReportType).map((t) => ({
		key: t,
		title: <Typography.Text strong>{ReportTypeLabelMap[t]}</Typography.Text>,
	})),
	getColumnKey: (report) => report.type as ReportType,
	getRowKey: (report) => report.id as string,
	renderCard: (report) => (
		<Flex vertical gap={4}>
			<Flex justify="space-between" align="center" gap={8}>
				<Typography.Text strong ellipsis style={{ minWidth: 0 }}>
					{report.title}
				</Typography.Text>
				<ReportStatusBadge status={report.status as ReportStatus} />
			</Flex>
			<DateField value={report.createdAt as string} />
		</Flex>
	),
};

interface ReportTableProps
	extends Omit<ResponsiveTableProps<ArtemisReport>, "columns"> {
	onDeleteReports?: (reports: ArtemisReport[]) => void;
	// Controlled server-side search/filter (owned by the route via usePagedList).
	search?: string;
	onSearchChange?: (v: string) => void;
	type?: string;
	onTypeChange?: (v: string | undefined) => void;
	status?: string;
	onStatusChange?: (v: string | undefined) => void;
}

export function ReportTable({
	onDeleteReports,
	loading,
	search,
	onSearchChange,
	type,
	onTypeChange,
	status,
	onStatusChange,
	...props
}: ReportTableProps) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.xl;

	const columns: ColumnDef<ArtemisReport>[] = useMemo(
		() => [
			{
				accessorKey: "title",
				header: "Title",
				filterFn: searchFilter,
				cell: ({ row }) => (
					<strong style={{ cursor: "pointer" }}>{row.original.title}</strong>
				),
			},
			{
				accessorKey: "type",
				header: "Type",
				size: 130,
				enableSorting: true,
				filterFn: "equals",
				cell: ({ row }) => (
					<ReportTypeBadge type={row.original.type as ReportType} />
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				size: 110,
				enableSorting: true,
				filterFn: "equals",
				cell: ({ row }) => (
					<ReportStatusBadge status={row.original.status as ReportStatus} />
				),
			},
			//   {
			// 	accessorKey: "id",
			// 	header: "ID",
			// 	size: 200,
			// 	cell: ({ row }) => (
			// 		<div style={{ display: "flex", alignItems: "center" }}>
			// 			<IdField id={row.original.id as string} />
			// 		</div>
			// 	),
			// },
			{
				accessorKey: "createdAt",
				header: "Date",
				size: 120,
				enableSorting: true,
				cell: ({ row }) => (
					<DateField value={row.original.createdAt as string} />
				),
			},
		],
		[],
	);

	const confirmDelete = (reports: ArtemisReport[]) =>
		confirmDeleteResource(() => onDeleteReports?.(reports));

	return (
		<ResponsiveTableKanban<ArtemisReport, ReportType>
			kanban={KANBAN}
			loading={loading}
			columns={columns}
			rowActions={[
				{
					label: "View",
					icon: <CustomIcon icon={IconEye} />,
					onClick: (report) =>
						navigate({
							to: `/${orgSlug}/workspaces/${report.workspaceId}/reports/${report.id}` as "/",
						}),
				},
				...(onDeleteReports
					? [
							{
								label: "Delete",
								icon: <CustomIcon icon={IconTrash} />,
								danger: true,
								onClick: (report: ArtemisReport) => confirmDelete([report]),
							},
						]
					: []),
			]}
			initialSorting={[{ id: "createdAt", desc: true }]}
			compactRender={(report) => (
				<article
					onClick={() =>
						navigate({
							to: `/${orgSlug}/workspaces/${report.workspaceId}/reports/${report.id}` as "/",
						})
					}
					onKeyDown={() =>
						navigate({
							to: `/${orgSlug}/workspaces/${report.workspaceId}/reports/${report.id}` as "/",
						})
					}
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<Typography.Text strong ellipsis>
						{report.title}
					</Typography.Text>
					<ReportStatusBadge status={report.status as ReportStatus} />
				</article>
			)}
			filterBar={() => (
				<TableFilterBar isCompact={isCompact}>
					<Input.Search
						placeholder="Search by title or ID"
						allowClear
						style={{ width: isCompact ? "100%" : 220 }}
						value={search ?? ""}
						onChange={(e) => onSearchChange?.(e.target.value)}
						onSearch={(v) => onSearchChange?.(v)}
					/>
					<Select
						allowClear
						placeholder="Type"
						style={{ width: isCompact ? "100%" : 150 }}
						value={type ?? null}
						onChange={(v) => onTypeChange?.(v ?? undefined)}
						options={Object.values(ReportType).map((t) => ({
							value: t,
							label: ReportTypeLabelMap[t],
						}))}
					/>
					<Select
						allowClear
						placeholder="Status"
						style={{ width: isCompact ? "100%" : 130 }}
						value={status ?? null}
						onChange={(v) => onStatusChange?.(v ?? undefined)}
						options={Object.values(ReportStatus).map((s) => ({
							value: s,
							label: ReportStatusLabelMap[s],
						}))}
					/>
				</TableFilterBar>
			)}
			onRowClick={(report) =>
				navigate({
					to: `/${orgSlug}/workspaces/${report.workspaceId}/reports/${report.id}` as "/",
				})
			}
			rowHref={(report) =>
				`/${orgSlug}/workspaces/${report.workspaceId}/reports/${report.id}`
			}
			selectionAction={
				onDeleteReports
					? {
							label: "Delete",
							icon: <CustomIcon icon={IconTrash} />,
							variant: "destructive",
							onClick: confirmDelete,
							disabled: loading,
						}
					: undefined
			}
			{...props}
		/>
	);
}
